import React, { useCallback, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
  ClosePanelButton,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalSize,
  NumberInput,
} from 'venn-components';
import { compact } from 'lodash';
import { CheckboxTree, ColorUtils, flattenTree, GetColor, Icon, Tooltip, type TreeNode } from 'venn-ui-kit';
import {
  analysisSubjectQuery,
  excludedFees,
  subjectInputGroupSubjects,
  type SubjectInputId,
  type SubjectWithOptionalFeeAndFilters,
  useCachedLoadableValue,
  workspaceFeeLimitsState,
  type WorkspaceFeeLimits,
} from 'venn-state';
import { useFees } from './useFees';
import { isCashNode, isStrategyNode, useHasFF } from 'venn-utils';
import type { Portfolio } from 'venn-api';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { getCanSaveStatusAndOptionalError, getNodeContent } from './ManageFeesModal';
import styled from 'styled-components';

export interface EditFeesModalProps {
  subjectInputId: SubjectInputId;
  subject: SubjectWithOptionalFeeAndFilters;
  idx: number;
  onClose: () => void;
}

const FEE_MIN_PCT = 0;
const FEE_MAX_PCT = 100;

const portfolioToTreeNode = (
  portfolio: Portfolio,
  filteredSubjectIds: string[] = [],
  isFilteredParent: boolean = false,
): TreeNode<React.ReactNode> | undefined => {
  const { children, id } = portfolio;
  const isStrategy = isStrategyNode(portfolio);
  const isFiltered = filteredSubjectIds.length === 0 || isFilteredParent || filteredSubjectIds.includes(id.toString());
  const childNodes = compact(children.map((c) => portfolioToTreeNode(c, filteredSubjectIds, isFiltered)));
  if (isStrategy && childNodes.length === 0) {
    return undefined;
  }
  const isCash = isCashNode(portfolio);
  return {
    children: childNodes,
    data: getNodeContent(portfolio),
    key: id.toString(10),
    selectedOverride: isCash ? false : isFiltered ? undefined : false,
    tooltip: isCash
      ? (portfolio.netAssets ?? 0) < 0
        ? 'Unable to apply fees to cash borrowing.'
        : 'Unable to apply fees to cash holding.'
      : !isFiltered
        ? 'Portfolio filter excludes this investment, thus fee exclusions do not apply.'
        : undefined,
  };
};

const getFlattenedOverridesMap = (node: TreeNode<React.ReactNode> | undefined): Record<string, boolean | undefined> => {
  const overrides: Record<string, boolean | undefined> = {};
  const traverse = (currentNode: TreeNode<React.ReactNode> | undefined) => {
    if (!currentNode) return;
    overrides[currentNode.key] = currentNode.selectedOverride;
    currentNode.children?.forEach(traverse);
  };
  traverse(node);
  return overrides;
};

const FeeLimitsTooltipContent = ({ limits }: { limits: WorkspaceFeeLimits }) => (
  <div className="p-2 text-[12px]">
    <div className="font-bold mb-2">Workspace limits</div>
    <div className="flex flex-col gap-1">
      <div className="flex justify-between gap-4">
        <span>Default:</span>
        <span className="font-bold">{limits.defaultFee.toFixed(2)}%</span>
      </div>
      <div className="flex justify-between gap-4">
        <span>Min:</span>
        <span className="font-bold">{limits.minFee.toFixed(2)}%</span>
      </div>
      <div className="flex justify-between gap-4">
        <span>Max:</span>
        <span className="font-bold">{limits.maxFee.toFixed(2)}%</span>
      </div>
    </div>
  </div>
);

export const EditFeesModal = ({ subjectInputId, subject, idx, onClose }: EditFeesModalProps) => {
  const groupIds = [subjectInputId];
  const [baselineFees, setBaselineFees] = useFees(groupIds);
  const [fees, setFees] = useState(baselineFees);
  const baselineExclusions = useRecoilValue(excludedFees(groupIds));
  const [selectedExclusions, _setSelectedExclusions] = useState<Record<SubjectInputId, string[][]>>(baselineExclusions);
  const workspaceFeeLimits = useRecoilValue(workspaceFeeLimitsState);
  const hasDefaultRlAdvisoryFeesFF = useHasFF('default_rl_advisory_fees_ff');

  const setSelectedExclusionsByKeys = (selectedKeys: string[]) => {
    const newSelectedExclusions = { ...selectedExclusions };
    newSelectedExclusions[subjectInputId] = [...(newSelectedExclusions[subjectInputId] ?? [])];
    newSelectedExclusions[subjectInputId][idx] = selectedKeys;
    _setSelectedExclusions(newSelectedExclusions);
  };
  const inputSupportsFees: Record<SubjectInputId, boolean[]> = {
    [subjectInputId]: (fees[subjectInputId] ?? []).map((_, i) => i === idx),
  };
  const {
    isValid: isFeeInputValid,
    canSave,
    saveDisabledTooltipMessage: saveDisabledTooltip,
    errorBannerMessage: errorBanner,
  } = getCanSaveStatusAndOptionalError(
    fees,
    inputSupportsFees,
    baselineFees,
    selectedExclusions,
    baselineExclusions,
    workspaceFeeLimits,
    hasDefaultRlAdvisoryFeesFF,
  );
  const subjectAnalysisSubjectQuery = analysisSubjectQuery(subject);
  const analysisSubject = useCachedLoadableValue(subjectAnalysisSubjectQuery, undefined);
  const portfolio = analysisSubject!.portfolio;
  const fund = analysisSubject!.fund;
  const isSubjectAnInvestment = !portfolio && analysisSubject!.fund?.id;
  const groupSubjects = useRecoilValue(subjectInputGroupSubjects(subjectInputId));
  const filteredSubjectIds = isSubjectAnInvestment
    ? []
    : (groupSubjects?.find((g) => g.type === 'portfolio' && g.portfolioId === portfolio!.id)?.filteredSubjectIds ?? []);
  const portfolioNodes = portfolio ? portfolioToTreeNode(portfolio, filteredSubjectIds, false) : undefined;
  const flattenedPortfolioIdToSelectedOverrideMap = getFlattenedOverridesMap(portfolioNodes);
  const excludedFeesQtty =
    selectedExclusions[subjectInputId]?.[idx]?.filter((i) => flattenedPortfolioIdToSelectedOverrideMap[i] !== false)
      .length ?? 0;

  const rowRenderer: React.ComponentProps<typeof CheckboxTree<React.ReactNode>>['rowRenderer'] = useCallback(
    ({ level, key, data, isLeaf, selected, onSelect, selectedOverride, tooltip }) => (
      <div
        className={cn('my-[4px] flex items-center', !isLeaf && 'font-bold', 'text-[12px] pt-[4px]')}
        style={{ marginLeft: `${16 * (level - 1)}px` }}
      >
        {isLeaf ? (
          <>
            <Checkbox
              id={key}
              state={selectedOverride || !selected ? 'checked' : 'unchecked'}
              onCheckedChange={() => onSelect(key)}
              disabled={selectedOverride !== undefined}
            />
            <label
              className={cn('px-[3px] font-normal', selectedOverride !== undefined ? ' text-venn-grey-50' : '')}
              htmlFor={key}
            >
              <Tooltip content={tooltip} usePortal>
                {data}
              </Tooltip>
            </label>
          </>
        ) : (
          data
        )}
      </div>
    ),
    [],
  );

  return (
    <Modal className="h-[55rem] flex flex-col" onClose={onClose} size={ModalSize.Large} closeOnEsc noPadding>
      <ModalHeader className="bg-venn-grey-10 flex flex-row justify-between items-start m-0 py-[11px] px-[16px]">
        <span className="text-venn-grey-80 font-bold text-base !text-[14px] ">EDIT FEES</span>
        <ClosePanelButton onClose={onClose} data-testid="qa-close-upload-panel" />
      </ModalHeader>
      {errorBanner && (
        <StyledBannerMessage>
          <Icon type="triangle-exclamation" className="text-venn-error" /> {errorBanner}
        </StyledBannerMessage>
      )}
      <ModalContent
        className="border-t border-b overflow-y-scroll border-venn-grey-30 pt-[9px] !px-[16px] flex-1"
        style={{ borderTopStyle: 'solid', borderBottomStyle: 'solid' }}
      >
        <div className="flex justify-between">
          <span className="text-[14px] pt-[6px]">
            <Icon
              className={isSubjectAnInvestment ? 'text-venn-investment-gold' : 'text-venn-deep-dark-blue'}
              type={isSubjectAnInvestment ? 'square' : 'folder'}
            />{' '}
            {isSubjectAnInvestment ? fund?.name : portfolio?.name}
          </span>
          <span className="flex items-center gap-1">
            {hasDefaultRlAdvisoryFeesFF && workspaceFeeLimits && (
              <Tooltip content={<FeeLimitsTooltipContent limits={workspaceFeeLimits} />} usePortal>
                <Icon type="info-circle" prefix="far" className="text-venn-grey-80 cursor-pointer" />
              </Tooltip>
            )}
            <NumberInput
              align="right"
              inputClassName="pr-[4px]"
              label={`${portfolio?.name ?? fund?.name ?? ''} fees`}
              data-testid="qa-fees-input"
              min={FEE_MIN_PCT}
              max={FEE_MAX_PCT}
              precision={2}
              step={0.1}
              width={70}
              height={30}
              error={!isFeeInputValid}
              onChange={(value: number) => {
                setFees({
                  ...fees,
                  [subjectInputId]: [
                    ...fees[subjectInputId]!.slice(0, idx),
                    value / 100,
                    ...fees[subjectInputId]!.slice(idx + 1),
                  ],
                });
              }}
              unitSymbol="%"
              value={(fees[subjectInputId]![idx] ?? 0) * 100}
              controlled
            />
          </span>
        </div>
        {portfolio && (
          <>
            <div className="my-[6px]">
              <span className="text-venn-grey-80 text-base">{FEE_EXCLUSION_INSTRUCTIONS}</span>
            </div>
            <CheckboxTree
              selectedKeys={selectedExclusions[subjectInputId]?.[idx] ?? []}
              setSelectedKeys={(newSelectedKeys) => setSelectedExclusionsByKeys(newSelectedKeys)}
              rows={flattenTree(portfolioNodes!).slice(1)}
              rowRenderer={rowRenderer}
            />
          </>
        )}
      </ModalContent>
      <ModalFooter
        cancelLabel="CANCEL"
        className="!bg-venn-white !border-t-0 mb-[6px] mt-[6px] px-[12px]"
        primaryLabel="APPLY"
        rightChildren={!isSubjectAnInvestment && <span className="text-[12px]">{excludedFeesQtty} excluded</span>}
        simplifiedButtonStyles
        onCancel={onClose}
        onPrimaryClick={() => {
          setBaselineFees(fees, selectedExclusions);
          onClose();
        }}
        primaryDisabled={!canSave}
        primaryTooltip={saveDisabledTooltip}
      />
    </Modal>
  );
};

const FEE_EXCLUSION_INSTRUCTIONS = `
Uncheck investments to exclude them from the applied advisory fee. When investments are excluded, a
footnote will be added to each page directing readers to the Disclosures page, where all excluded
investments will be detailed.
`;

const StyledBannerMessage = styled.span`
  display: flex;
  padding: 8px 12px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  font-size: 12px;
  background-color: ${ColorUtils.opacifyFrom(GetColor.Error, 0.1)};
`;
